import React, { useState } from 'react';
import AnimatedWrapper from './AnimatedWrapper';
import { VerifiedIcon } from './icons/VerifiedIcon';
import { DocumentTextIcon } from './icons/DocumentTextIcon';
import { ChatBubbleIcon } from './icons/ChatBubbleIcon';
import { KeyIcon } from './icons/KeyIcon';
import { QuoteIcon } from './icons/QuoteIcon';
import { ChevronDownIcon } from './icons/ChevronDownIcon';

interface ListPropertyLandingProps {
    onGetStartedClick: () => void;
    children?: React.ReactNode;
}

const howItWorksSteps = [
    {
        icon: <DocumentTextIcon className="w-10 h-10 text-brand-primary" />,
        title: '1. Submit Your Details',
        description: 'Fill out our simple online form with your property and contact information. It only takes a few minutes.',
    },
    {
        icon: <VerifiedIcon className="w-10 h-10 text-brand-primary" />,
        title: '2. Verification & Listing',
        description: 'Our team verifies your property documents and details, then creates a professional, appealing listing for you.',
    },
    {
        icon: <ChatBubbleIcon className="w-10 h-10 text-brand-primary" />,
        title: '3. Connect with Buyers',
        description: 'Receive inquiries directly from genuine, verified buyers. We handle the initial screening to save you time.',
    },
    {
        icon: <KeyIcon className="w-10 h-10 text-brand-primary" />,
        title: '4. Close The Deal',
        description: 'Finalize your deal with confidence. Our team provides end-to-end support to ensure a smooth and secure transaction.',
    },
];

const faqs = [
    {
        question: "What are the fees for listing my property?",
        answer: "Basic property listing is free. We also offer premium services like the SellAny Concierge package, which includes professional photography, marketing, and site visit coordination for a nominal fee. There are absolutely no hidden brokerage charges."
    },
    {
        question: "How long does the verification process take?",
        answer: "Our verification process is thorough to ensure trust and transparency. It typically takes 2-4 business days, after which your listing will go live on our platform."
    },
    {
        question: "Do I have to manage site visits myself?",
        answer: "You can manage them yourself, or you can opt for our SellAny Concierge service. With this service, our dedicated team will coordinate and conduct site visits on your behalf, which is especially helpful for NRI and busy owners."
    },
    {
        question: "How do you find genuine buyers?",
        answer: "We promote your property across multiple online platforms and our own network to attract a wide range of potential buyers. Our team then qualifies these leads to ensure you only connect with serious, genuine buyers."
    }
];

const AccordionItem = ({ question, answer }: { question: string; answer: string }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="border-b border-gray-200 py-4">
            <button
                className="w-full flex justify-between items-center text-left text-lg font-medium text-gray-900"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span>{question}</span>
                <ChevronDownIcon className={`w-6 h-6 transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            <div className={`grid overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                <div className="overflow-hidden">
                    <p className="pt-4 text-base text-gray-600">
                        {answer}
                    </p>
                </div>
            </div>
        </div>
    );
};

const ListPropertyLanding: React.FC<ListPropertyLandingProps> = ({ onGetStartedClick, children }) => {
    return (
        <>
            {/* Hero Section */}
            <section className="relative pt-28 pb-12 sm:pt-32 sm:pb-16 text-white flex items-end min-h-[500px] lg:min-h-[60vh]">
                <div className="absolute inset-0" style={{ backgroundImage: "url('https://i.ibb.co/dJkVFPZF/view-city-with-buildings-trees.jpg')", backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundSize: 'Cover' }}></div>
                <div className="absolute inset-0 bg-black/60"></div>
                <div className="container mx-auto px-3 sm:px-4 lg:px-6 relative z-10">
                    <AnimatedWrapper>
                        <div className="text-left max-w-4xl">
                            <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight">
                                Sell Your Property, The Smart Way
                            </h1>
                            <p className="mt-4 text-base sm:text-lg text-gray-300">
                                Join Kerala's most trusted property platform and connect directly with genuine buyers. It's simple, transparent, and broker-free.
                            </p>
                            <div className="mt-8">
                                <button
                                    onClick={onGetStartedClick}
                                    className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-full shadow-sm text-brand-primary bg-white hover:bg-gray-100 transition-all duration-300 transform hover:scale-105"
                                >
                                    List Your Property Now
                                </button>
                            </div>
                        </div>
                    </AnimatedWrapper>
                </div>
            </section>

            {/* Injected Content (Form) */}
            {children}

            {/* How It Works Section */}
            <section className="py-12 sm:py-16 bg-white">
                <div className="container mx-auto px-3 sm:px-4 lg:px-6">
                    <AnimatedWrapper>
                        <div className="text-center">
                            <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight">
                                A Simple Path to a Successful Sale
                            </h2>
                            <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-500">
                                We've streamlined the selling process to be as easy and transparent as possible.
                            </p>
                        </div>
                    </AnimatedWrapper>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                        {howItWorksSteps.map((step, index) => (
                            <AnimatedWrapper key={step.title} delay={index * 150}>
                                <div className="text-center">
                                    <div className="flex items-center justify-center h-20 w-20 rounded-2xl bg-gray-100 mx-auto">
                                        {step.icon}
                                    </div>
                                    <h3 className="mt-6 text-xl font-semibold text-gray-900">{step.title}</h3>
                                    <p className="mt-2 text-base text-gray-600">{step.description}</p>
                                </div>
                            </AnimatedWrapper>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why List With Us Section */}
            <section className="py-12 sm:py-16 bg-gray-50">
                <div className="container mx-auto px-3 sm:px-4 lg:px-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <AnimatedWrapper>
                            <div className="rounded-2xl overflow-hidden shadow-xl">
                                <img loading="lazy" src="https://images.unsplash.com/photo-1484154218962-a197022b5858?q=80&w=2074&auto-format&fit=crop"
                                    alt="Person listing property online"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </AnimatedWrapper>
                        <AnimatedWrapper delay={200}>
                            <div>
                                <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight">Why List with SellAny?</h2>
                                <p className="mt-4 text-lg text-gray-600">We offer more than just a listing. We provide a complete selling solution designed for the modern property owner.</p>
                                <ul className="mt-8 space-y-4">
                                    <li className="flex items-start">
                                        <VerifiedIcon className="w-6 h-6 mr-3 text-green-500 flex-shrink-0 mt-1" />
                                        <div>
                                            <h4 className="font-semibold text-gray-800">Maximum Visibility</h4>
                                            <p className="text-gray-600">We promote your property across our website, social media, and partner networks to reach thousands of potential buyers.</p>
                                        </div>
                                    </li>
                                    <li className="flex items-start">
                                        <ChatBubbleIcon className="w-6 h-6 mr-3 text-green-500 flex-shrink-0 mt-1" />
                                        <div>
                                            <h4 className="font-semibold text-gray-800">Genuine Buyers Only</h4>
                                            <p className="text-gray-600">Our team screens all inquiries, so you only deal with serious, qualified buyers, saving you valuable time and effort.</p>
                                        </div>
                                    </li>
                                    <li className="flex items-start">
                                        <KeyIcon className="w-6 h-6 mr-3 text-green-500 flex-shrink-0 mt-1" />
                                        <div>
                                            <h4 className="font-semibold text-gray-800">End-to-End Support</h4>
                                            <p className="text-gray-600">From professional photography and legal verification to coordinating site visits, we're with you at every step.</p>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </AnimatedWrapper>
                    </div>
                </div>
            </section>

            {/* Seller Testimonials Section */}
            <section className="py-12 sm:py-16 bg-brand-primary text-white">
                <div className="container mx-auto px-3 sm:px-4 lg:px-6">
                    <AnimatedWrapper>
                        <div className="text-center">
                            <h2 className="text-3xl font-semibold tracking-tight">
                                Success Stories from Sellers Like You
                            </h2>
                        </div>
                    </AnimatedWrapper>
                    <div className="mt-8 max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
                        <AnimatedWrapper>
                            <div className="bg-white/15 backdrop-blur-sm border border-white/20 p-8 rounded-2xl flex flex-col h-full">
                                <QuoteIcon className="w-10 h-10 text-white/50 mb-4" />
                                <p className="flex-grow text-gray-100 italic">"Finally, a real estate platform in Kerala that you can actually trust. The direct connection with owners is a game-changer. I sold my plot in Kozhikode in just two weeks!"</p>
                                <div className="mt-6">
                                    <p className="font-semibold text-lg">Rajesh Kumar</p>
                                    <p className="text-sm text-gray-200">Property Seller, Kozhikode</p>
                                </div>
                            </div>
                        </AnimatedWrapper>
                        <AnimatedWrapper delay={200}>
                            <div className="bg-white/15 backdrop-blur-sm border border-white/20 p-8 rounded-2xl flex flex-col h-full">
                                <QuoteIcon className="w-10 h-10 text-white/50 mb-4" />
                                <p className="flex-grow text-gray-100 italic">"As an NRI, selling my apartment seemed daunting. The SellAny Concierge service was a blessing. They handled everything from photos to site visits. The entire process was seamless."</p>
                                <div className="mt-6">
                                    <p className="font-semibold text-lg">Anitha Menon</p>
                                    <p className="text-sm text-gray-200">Property Seller, Dubai</p>
                                </div>
                            </div>
                        </AnimatedWrapper>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-12 sm:py-16 bg-white">
                <div className="container mx-auto px-3 sm:px-4 lg:px-6">
                    <AnimatedWrapper>
                        <div className="text-center">
                            <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight">
                                Frequently Asked Questions
                            </h2>
                        </div>
                    </AnimatedWrapper>
                    <div className="mt-8 max-w-3xl mx-auto">
                        {faqs.map((faq, index) => (
                            <AnimatedWrapper key={index} delay={index * 100}>
                                <AccordionItem question={faq.question} answer={faq.answer} />
                            </AnimatedWrapper>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
};

export default ListPropertyLanding;